<script setup lang="ts">
const route = useRoute();

useHead({
  title: 'Customer Testimonials | Hear from ZapScale Customers',
  meta: [
    {
      name: 'description',
      content:
        'Discover how ZapScale has helped businesses scale and succeed. Read real customer testimonials about why they love ZapScale.',
    },
    {
      property: 'og:description',
      content:
        'Discover how ZapScale has helped businesses scale and succeed. Read real customer testimonials about why they love ZapScale.',
    },
    {
      property: 'og:title',
      content: 'Customer Testimonials | Hear from ZapScale Customers',
    },
    {
      property: 'og:url',
      content: 'https://www.zapscale.com' + route.path,
    },
    {
      property: 'og:image',
      content: 'https://www.zapscale.com/images/logo.png',
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.zapscale.com' + route.path,
    },
  ],
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ZapScale',
  description:
    'A single-source customer success management SaaS platform with 50 pre-built playbooks, 40 out-of-the-box KPIs, and unlimited user logins.',
  url: 'https://www.zapscale.com/',
  logo: 'https://www.zapscale.com/images/Header_logo.svg',
  sameAs: [
    'https://www.facebook.com/zapscale',
    'https://www.linkedin.com/company/zapscale/',
    'https://twitter.com/ZapScale',
  ],
}));

const customerReviewCards = [
  {
    customerImg: 'Jitendra-Singh',
    customerImgWidth: '368',
    customerImgHeight: '565',
    imgBgClass: 'bg-[#941651]',
    title: 'Efficient and insightful — ZapScale has been a great addition!',
    titleClass: 'text-[#941651] font-oswald',
    accentClass: 'bg-[#941651]',
    subtitleHtml:
      '<span class="font-medium">ZapScale has helped us boost our Customer Success efforts by giving us better insights and automating repetitive tasks.<span class="font-bold text-[#941651]">The health metrics and smart segmentation features are incredibly useful.</span></span>',
    textBgClass: 'bg-[#FEEBF4]',
    customerName: 'Jitendra Singh',
    customerNameClass: 'text-[#941651]',
    position: 'Product Manager, Tagbox',
  },
  {
    customerImg: 'Matthew-Presley',
    customerImgWidth: '368',
    customerImgHeight: '522',
    imgBgClass: 'bg-[#0070C0]',
    title: 'Fantastic Team',
    titleClass: 'text-[#009193] font-oswald',
    accentClass: 'bg-[#009193]',
    subtitleHtml:
      '<span class="font-medium">Fantastic supportive team that really cares for their customers. Zapscale gives a <span class="font-bold text-[#009193]">nice overview of our customer base!</span></span>',
    textBgClass: 'bg-[#E9F6FF]',
    customerName: 'Matthew Presley',
    customerNameClass: 'text-[#009193]',
    position: 'Customer Success Manager, Inven',
  },
  {
    customerImg: 'Shreekumar-Nair',
    customerImgWidth: '368',
    customerImgHeight: '525',
    imgBgClass: 'bg-[#332C5F]',
    title: 'Improved Decision-Making Accuracy, Data Visibility, and Automation',
    titleClass: 'text-[#FF395B] font-oswald',
    accentClass: 'bg-[#FF395B]',
    subtitleHtml:
      '<span class="font-medium">The platform streamlined reporting processes,<span class="text-[#FF395B] font-bold"> reducing quarterly workloads</span>, and standardized <span class="text-[#FF395B] font-bold">KPIs, enhancing productivity.</span></span>',
    textBgClass: 'bg-[#DDD7FF]',
    customerName: 'Shreekumar Nair',
    customerNameClass: 'text-[#FF395B]',
    position: 'AVP - Customer Success, Xebo.ai ',
  },
  {
    customerImg: 'Pankkhaj-Amesar',
    customerImgWidth: '368',
    customerImgHeight: '529',
    imgBgClass: 'bg-[#F9B92D]',
    title: 'A Unified Platform For Tracking Key Customer Aspects',
    titleClass: 'text-[#0070C0] font-oswald',
    accentClass: 'bg-[#0070C0]',
    subtitleHtml:
      '<span class="font-medium">ZapScale provides a unified platform for tracking key aspects like features, health scores, issues, and commercial data, all in one place. Additionally, it has <span class="text-[#0070C0] font-bold"> automated alerts for potential risks in client health</span>, keeping us proactive. </span>',
    textBgClass: 'bg-[#FFE6BC]',
    customerName: 'Pankaj Amesar',
    customerNameClass: 'text-[#0070C0]',
    position: 'Customer Success Manager, BrandIntelle',
  },
  {
    customerImg: 'Jijo-Mulankattil',
    customerImgWidth: '368',
    customerImgHeight: '557',
    imgBgClass: 'bg-[#6CBB76]',
    title: 'Saves me time and effort!',
    titleClass: 'text-[#FF3B30] font-oswald',
    accentClass: 'bg-[#FF3B30]',
    subtitleHtml: `<span class='font-medium'>With ZapScale in place, I'm able to <span class="font-bold text-[#FF3B30]">see exactly where each customer stands</span> without scouring through a ton of data saving me time and effort.</span>`,
    textBgClass: 'bg-[#E0FFE4]',
    customerName: 'Jijo Mulankattil',
    customerNameClass: 'text-[#FF3B30]',
    position: 'Customer Success Manager, AdIntelle',
  },
  {
    customerImg: 'Malvika-Sridhar',
    customerImgWidth: '368',
    customerImgHeight: '556',
    imgBgClass: 'bg-[#332C5F]',
    title: 'Invaluable Tool For Any Customer Success Team',
    titleClass: 'text-[#FF395B] font-oswald',
    accentClass: 'bg-[#FF395B]',
    subtitleHtml:
      '<span class="font-medium">ZapScale and team provide outstanding support, diligently addressing our needs and welcoming customization requests. Their personalized approach has enabled us to gain <span class="font-bold text-[#FF395B]">valuable insights that are specifically aligned with our requirements.</span></span>',
    textBgClass: 'bg-[#DDD7FF]',
    customerName: 'Malvika Sridhar',
    customerNameClass: 'text-[#FF395B]',
    position: 'Customer Success Manager, SimpliSmart',
  },
  {
    customerImg: 'Gaurav-Sinha',
    customerImgWidth: '368',
    customerImgHeight: '527',
    imgBgClass: 'bg-[#F9B92D]',
    title: 'Addresses Adoption Gaps And Optimizes Customer Journeys',
    titleClass: 'text-[#0070C0] font-oswald',
    accentClass: 'bg-[#0070C0]',
    subtitleHtml:
      '<span class="font-medium">With actionable insights into product usage, I can proactively address adoption gaps and optimize customer journeys. Automated nudges on critical red-line cases <span class="font-bold text-[#0070C0]">ensure timely interventions, reducing churn risk.</span></span>',
    textBgClass: 'bg-[#FFE6BC]',
    customerName: 'Gaurav Sinha',
    customerNameClass: 'text-[#0070C0]',
    position: 'VP - Customer Success, SingleInterface',
  },
  {
    customerImg: 'Toshiba-Thaldi',
    customerImgWidth: '368',
    customerImgHeight: '573',
    imgBgClass: 'bg-[#941651]',
    title: 'Seamless and Intuitive',
    titleClass: 'text-[#941651] font-oswald',
    accentClass: 'bg-[#941651]',
    subtitleHtml:
      '<span class="font-medium">We were looking for a customer success tool that integrates our data seamlessly and shows us valuable insights. ZapScale is intuitive and has helped our team to use their <span class="font-bold text-[#941651]">predictive analytics to track customer journeys and enhance our CS strategy.</span></span>',
    textBgClass: 'bg-[#FEEBF4]',
    customerName: 'Toshiba Thaldi',
    customerNameClass: 'text-[#941651]',
    position: 'Director – Customer Success, PeopleStrong',
  },
  {
    customerImg: 'Eugene-Kingsley',
    customerImgWidth: '368',
    customerImgHeight: '598',
    imgBgClass: 'bg-[#0070C0]',
    title: 'Love it!',
    titleClass: 'text-[#009193] font-oswald',
    accentClass: 'bg-[#009193]',
    subtitleHtml:
      '<span class="font-medium">Customer Success metrics are our love language, and Zapscale speaks it fluently with <span class="font-bold text-[#009193]">great insights.</span> They help us reduce churn like pros! <span class="font-bold text-[#009193]">nice overview of our customer base!</span></span>',
    textBgClass: 'bg-[#E9F6FF]',
    customerName: 'Eugene Kingsley',
    customerNameClass: 'text-[#009193]',
    position: 'CS - Strategy and Ops, Zluri',
  },
  {
    customerImg: 'Deepak-Bansal',
    customerImgWidth: '368',
    customerImgHeight: '600',
    imgBgClass: 'bg-[#6CBB76]',
    title: 'ZapScale has been a game-changer!',
    titleClass: 'text-[#FF3B30] font-oswald',
    accentClass: 'bg-[#FF3B30]',
    subtitleHtml: `<span class='font-medium'>ZapScale <span class="font-bold text-[#FF3B30]">brings all usage data together</span>, giving me a clear overview of my accounts. With segments and KPIs, I can tailor my work and quickly see the health of each account. Automated alerts keep me on top of critical issues, making me <span class="font-bold text-[#FF3B30]">more proactive than ever before.</span></span>`,
    textBgClass: 'bg-[#E0FFE4]',
    customerName: 'Deepak Bansal',
    customerNameClass: 'text-[#FF3B30]',
    position: 'CS Head, Spyne.ai',
  },
  {
    customerImg: 'Akash-Saha',
    customerImgWidth: '368',
    customerImgHeight: '536',
    imgBgClass: 'bg-[#332C5F]',
    title: 'More features than you’ll ever need!',
    titleClass: 'text-[#FF395B] font-oswald',
    accentClass: 'bg-[#FF395B]',
    subtitleHtml: `<span class="font-medium">ZapScale offers more features than you'd ever think you'd need, making it <span class="font-bold text-[#FF395B]">highly versatile.</span> The ability to customize fields and segments allows it to adapt to our specific needs seamlessly. The new HubSpot integration is going to be a game-changer.</span>`,
    textBgClass: 'bg-[#DDD7FF]',
    customerName: 'Akash Saha',
    customerNameClass: 'text-[#FF395B]',
    position: 'Customer Success Manager, Astra',
  },
  {
    customerImg: 'Anshuman-Singh-Chauhan',
    customerImgWidth: '368',
    customerImgHeight: '561',
    imgBgClass: 'bg-[#F9B92D]',
    title: 'Great Usage Insights!',
    titleClass: 'text-[#0070C0] font-oswald',
    accentClass: 'bg-[#0070C0]',
    subtitleHtml:
      '<span class="font-medium">ZapScale provided valuable insights into the frequency of account usage, enabling me to <span class="font-bold text-[#0070C0]">effectively monitor and maintain account health.</span></span>',
    textBgClass: 'bg-[#FFE6BC]',
    customerName: 'Anshuman Singh Chauhan',
    customerNameClass: 'text-[#0070C0]',
    position: 'Customer Success Manager, Taggbox',
  },
  {
    customerImg: 'Bharath-Jagannath',
    customerImgWidth: '368',
    customerImgHeight: '525',
    imgBgClass: 'bg-[#941651]',
    title: 'Transformed by CS Strategy with ZapScale',
    titleClass: 'text-[#941651] font-oswald',
    accentClass: 'bg-[#941651]',
    subtitleHtml: `<span class="font-medium">With ZapScale, I've transformed my customer success strategy. It has helped us <span class="font-bold text-[#941651]">consolidate all our customer data</span> and make it centrally available to everyone. Also, it has been <span class="font-bold text-[#941651]">fantastic working with the Zapscale</span>, we love the collaboration and the sense of partnership that they bring to the table!</span>`,
    textBgClass: 'bg-[#FEEBF4]',
    customerName: 'Bharath Jagannath',
    customerNameClass: 'text-[#941651]',
    position: 'Associate Dir - CS Strategy & Ops, Zluri',
  },
  {
    customerImg: 'Prabha-Kumari',
    customerImgWidth: '368',
    customerImgHeight: '536',
    imgBgClass: 'bg-[#0070C0]',
    title: 'Simplifies my customer success',
    titleClass: 'text-[#009193] font-oswald',
    accentClass: 'bg-[#009193]',
    subtitleHtml:
      '<span class="font-medium">ZapScale simplifies Customer Success management by tracking CSM tasks, delivering usage data and KPI insights, detecting customer issues through ticket and billing monitoring, <span class="font-bold text-[#009193]">providing instant sentiment analysis</span>, and automating workflows to drive proactive success.</span>',
    textBgClass: 'bg-[#E9F6FF]',
    customerName: 'Prabha Kumari',
    customerNameClass: 'text-[#009193]',
    position: 'Customer Success Manager, Spyne.ai',
  },
];
</script>

<template>
  <div>
    <div class="bg-[#242424]">
      <div class="container mx-auto">
        <div class="h-[268px] px-5 pt-10 text-center lg:pt-16">
          <h1
            class="font-merriweather text-2xl font-extrabold text-white max-md:leading-7 md:text-3xl md:font-extrabold lg:text-5xl"
          >
            Delighting Our Customers Every Step Of The Way
          </h1>
          <NuxtLink
            to="/demo/#book-a-demo"
            class="mx-auto mt-8 flex h-[41px] w-fit items-center rounded-full bg-[#FF395B] px-3 py-2 text-xs font-bold text-white hover:border hover:border-[#FF395B] hover:bg-white hover:text-[#FF395B] md:px-6 md:text-base"
          >
            Book a Free Demo
          </NuxtLink>
        </div>
      </div>
    </div>
    <div class="bg-white">
      <div class="h-[200px] bg-[#242424]"></div>
      <div class="container top-8 mx-auto -mt-[250px] md:-mt-[200px]">
        <div class="grid grid-cols-1 gap-x-6 gap-y-6 px-5 lg:grid-cols-2">
          <div
            v-for="(card, index) in customerReviewCards"
            :key="card.title"
            class="col-span-1 grid grid-cols-2 lg:h-[480px]"
          >
            <div
              class="col-span-2 max-md:rounded-t-2xl md:col-span-1 md:rounded-bl-[20px] md:rounded-tl-[20px]"
              :class="card.imgBgClass"
            >
              <div
                class="relative flex h-full items-end justify-center lg:bg-[url('/images/customers-page/zapscale-logo-bg.svg')] lg:bg-left-bottom lg:bg-no-repeat"
              >
                <NuxtImg
                  :src="`/images/customers-page/${card.customerImg}.webp`"
                  :alt="card.customerName"
                  :loading="index > 1 ? 'lazy' : 'eager'"
                  class="absolute bottom-0 max-h-full lg:hidden"
                />
                <NuxtImg
                  :src="`/images/customers-page/${card.customerImg}-large.webp`"
                  :alt="card.customerName"
                  :width="card.customerImgWidth"
                  :height="card.customerImgHeight"
                  :loading="index > 1 ? 'lazy' : 'eager'"
                  class="absolute bottom-0 hidden rounded-bl-[20px] lg:block"
                />

                <NuxtImg
                  src="/images/customers-page/zapscale-logo-bg.svg"
                  alt="Zapscale"
                  width="744"
                  height="587"
                  class="w-full lg:hidden"
                />
              </div>
            </div>
            <div
              class="col-span-2 flex items-center justify-center py-5 max-md:rounded-b-2xl md:col-span-1 md:justify-start md:rounded-br-[20px] md:rounded-tr-[20px] md:py-4 lg:min-h-full lg:py-0"
              :class="card.textBgClass"
            >
              <div class="flex w-[92%] flex-col gap-y-4">
                <p class="pl-4 text-lg font-bold uppercase md:text-xl" :class="card.titleClass">
                  {{ card.title }}
                </p>
                <span class="block h-[3px] w-full md:h-[4px]" :class="card.accentClass"></span>
                <p v-html="card.subtitleHtml" class="pl-4 text-[17px]"></p>
                <div class="pl-4">
                  <p :class="['text-xl font-black', card.customerNameClass]">
                    {{ card.customerName }}
                  </p>
                  <p class="text-base">{{ card.position }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="py-10 md:py-14">
      <NuxtImg
        loading="lazy"
        :placeholder="[158, 22]"
        src="/images/landing-page/heart.svg"
        alt="Zapscale - Landing Page"
        width="158"
        height="22"
        class="mx-auto mb-6"
      />
      <CustomerLogoSlider />
    </div>
    <div class="bg-[#FFC52F]">
      <div class="container mx-auto">
        <div class="px-5 py-10 md:px-4">
          <h2
            class="mx-auto mb-6 block text-center font-merriweather text-2xl font-bold text-black md:mb-8 lg:text-3xl xl:w-[971px] xl:text-[35px] xl:leading-[50px]"
          >
            Simplify your customer success
            <span class="block lg:whitespace-nowrap"> operations with the easiest customer success platform. </span>
          </h2>
          <NuxtLink
            to="/demo/#book-a-demo"
            class="mx-auto block h-[41px] w-[179px] rounded-full bg-[#FF395B] px-6 py-2 text-base font-semibold text-white hover:border hover:border-[#FF395B] hover:bg-white hover:text-[#FF395B]"
          >
            <span class="flex items-center justify-between">
              Get Started
              <Icon name="solar:arrow-right-linear" />
            </span>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
